// src/components/Menu/MenuMobile.tsx

import React, { ComponentType, SVGProps } from 'react';
import styled from 'styled-components';
import { IoMenu } from 'react-icons/io5';

import { MEDIA_BREAKPOINTS } from '@/constants/layout';

import {
  ToggleThemeButton,
  SocialMediaMenu,
} from '@/components';

import ToggleLanguageButton from '@/components/ToggleLanguageButton/ToggleLanguageButton';
import { MenuButton } from '@/components/StyledComponents/Buttons';


// ==========================================================
// STYLES
// ==========================================================

const CollapsibleMenu = styled.div<{ $isActive: boolean }>`
  visibility: ${({ $isActive }) =>
    $isActive ? 'visible' : 'hidden'};
  opacity: ${({ $isActive }) =>
    $isActive ? 1 : 0};

  transform: translateY(
    ${({ $isActive }) =>
      $isActive ? '0' : '-10px'}
  );

  transition:
    visibility 0s,
    opacity 0.3s ease-out,
    transform 0.3s ease-out;

  display: flex;
  flex-direction: column;
  align-items: center;

  position: fixed;
  top: 50px;
  right: 15px;

  background-color: ${({ theme }) =>
    theme.menuColor};

  width: 140px;

  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  border: 2px solid
    ${({ theme }) => theme.mobileMenuIconBackground ?? theme.MenuItemSelectedColor};
  border-radius: 12px;

  z-index: 1100;
  padding: 6px 0;
`;

const MenuFooter = styled.div`
  width: 100%;
  border-top: 1px solid
    ${({ theme }) => theme.textColor}33;

  margin-top: 6px;
  padding: 6px;

  display: flex;
  justify-content: center;
  gap: 8px;
`;

const IoMenuIcon = styled(
  IoMenu as ComponentType<
    SVGProps<SVGSVGElement>
  >
)`
  cursor: pointer;

  position: fixed;
  top: 10px;
  right: 10px;

  z-index: 1100;

  color: ${({ theme }) => theme.iconColor};

  width: ${({ theme }) =>
    `calc(${theme.sizes.small.iconWidth} + 4px)`};
  height: ${({ theme }) =>
    `calc(${theme.sizes.small.iconHeight} + 4px)`};

  background: ${({ theme }) =>
    theme.mobileMenuIconBackground ??
    `linear-gradient(100deg, ${theme.gradientColors[0]}, ${theme.gradientColors[1]})`};

  border-radius: 50%;
  border: 2px solid
    ${({ theme }) => theme.backgroundColor};
`;

const MediaQuery = styled.div`
  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    display: block;
  }

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    display: none;
  }
`;

const SocialsContainer = styled.div`
  position: fixed;
  bottom: 10px;
  left: 0;
  z-index: 1100;
  display: flex;
  flex-direction: column;
`;

const MobileMenuButton = styled(MenuButton)`
  html:not(.rtl) & {
    font-size: 15px;
  }
`;

// ==========================================================
// TYPES
// ==========================================================

export type MenuMobileProps<T extends string = string> = {
  sections: {
    id: T;
    label: string;
  }[];

  isMenuOpen: boolean;
  toggleMenu: () => void;

  selectedSection: T;

  scrollToSection: (sectionId: T) => void;
};


// ==========================================================
// COMPONENT
// ==========================================================

const MenuMobile = <T extends string>({
  sections,
  isMenuOpen,
  toggleMenu,
  selectedSection,
  scrollToSection,
}: MenuMobileProps<T>) => {
  const handleMenuItemClick = (id: T) => {
    scrollToSection(id);

    const hash = `#${encodeURIComponent(id)}`;
    window.history.replaceState({}, '', hash);

    toggleMenu();
  };

  return (
    <MediaQuery>
      <IoMenuIcon
        onClick={toggleMenu}
        aria-label="menu"
      />

      <SocialsContainer>
        <SocialMediaMenu orientation="vertical" />
      </SocialsContainer>

      <CollapsibleMenu
        $isActive={isMenuOpen}
        role="navigation"
      >
        {/* ===== SECTIONS ===== */}
        {sections.map((s) => {
          const isActive =
            selectedSection === s.id;

          return (
            <MobileMenuButton
              key={s.id}
              $isSelected={isActive}
              onClick={() =>
                handleMenuItemClick(s.id)
              }
              aria-current={
                isActive ? 'page' : undefined
              }
            >
              {s.label}
            </MobileMenuButton>
          );
        })}

        {/* ===== FOOTER CONTROLS ===== */}
        <MenuFooter>
          <ToggleLanguageButton
            onToggle={toggleMenu}
          />

          <ToggleThemeButton
            onToggle={toggleMenu}
          />
        </MenuFooter>
      </CollapsibleMenu>
    </MediaQuery>
  );
};

export default MenuMobile;
